import {computed, inject, Injectable, signal} from '@angular/core';
import {ShortcutService} from './shortcut.service';
import {Shortcut} from './shortcut.model';
import {fromEvent} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ShortcutContext {
	private readonly service = inject(ShortcutService);

	private readonly shortcuts = signal<Shortcut[]>([]);
	readonly allShortcuts = computed(() => this.shortcuts());
	readonly activeShortcuts = computed(() =>
		this.shortcuts().filter(s => s.enabled ? s.enabled() : true)
	);

	readonly footerShortcuts = computed(() =>
		this.activeShortcuts().filter(s => s.showInFooter)
	);

	readonly helpShortcuts = computed(() =>
		this.shortcuts()
			.filter(s => s.showInHelp !== false)
			.sort((a, b) => (a.section ?? '').localeCompare(b.section ?? ''))
	);

	readonly helpShortcutsBySection = computed(() => {
		const grouped = new Map<string, Shortcut[]>();

		for (const s of this.helpShortcuts()) {
			const section = s.section ?? 'Generale';
			if (!grouped.has(section)) grouped.set(section, []);
			grouped.get(section)!.push(s);
		}

		return Array.from(grouped.entries());
	});

	constructor() {
		fromEvent<KeyboardEvent>(document, 'keydown')
			.subscribe(e => this.handleKey(e));
	}

	private handleKey(e: KeyboardEvent) {
		for (const s of this.activeShortcuts()) {
			if (this.matchShortcut(e, s)) {
				e.preventDefault();
				s.action();
				break;
			}
		}
	}

	private matchShortcut(e: KeyboardEvent, s: Shortcut): boolean {

		const key = e.key?.toUpperCase();
		if (!key) return false;

		const pressed: string[] = [];

		if (this.platform() !== 'MOBILE') {
			if (e.metaKey || e.ctrlKey) pressed.push('MOD');
		}

		pressed.push(key);

		return s.keys.length === pressed.length &&
			s.keys.every(k => pressed.includes(k));
	}

	register(shortcut: Shortcut) {
		this.shortcuts.update(s => [...s, shortcut]);
	}

	unregister(id: string) {
		this.shortcuts.update(s => s.filter(x => x.id !== id));
	}

	get modifier() {
		return this.service.modifierKey;
	}

	get platform() {
		return this.service.platform;
	}
}
